import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import AuthModal from './AuthModal';

const Navbar = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_balaji-hardware-1/artifacts/1ji3ijze_IMG-20250724-WA0021%281%29.jpg" 
                alt="Balaji Enterprises" 
                className="h-12 w-auto"
              />
              <span className="font-heading text-xl font-bold text-primary uppercase tracking-tight hidden sm:block">
                Balaji Enterprises
              </span>
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-sm font-medium transition-colors hover:text-secondary uppercase tracking-tight">
                Home
              </Link>
              <Link to="/products" className="text-sm font-medium transition-colors hover:text-secondary uppercase tracking-tight">
                Products
              </Link>
              <Link to="/contact" className="text-sm font-medium transition-colors hover:text-secondary uppercase tracking-tight">
                Contact
              </Link>
              {user && (
                <Link to="/orders" className="text-sm font-medium transition-colors hover:text-secondary uppercase tracking-tight">
                  My Orders
                </Link>
              )}
              {user?.role === 'admin' ? (
                <Link to="/admin" className="text-sm font-medium text-secondary bg-secondary/10 px-3 py-1 rounded-sm hover:bg-secondary/20 uppercase tracking-tight">
                  Admin Panel
                </Link>
              ) : (
                <Link to="/admin/login" className="text-sm font-medium text-slate-500 hover:text-secondary uppercase tracking-tight">
                  Admin
                </Link>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <Link to="/cart" className="relative" data-testid="cart-icon">
                <ShoppingCart className="h-6 w-6 text-primary hover:text-secondary transition-colors" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-secondary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              {user ? (
                <div className="hidden md:flex items-center space-x-2">
                  <span className="text-sm font-medium">{user.name}</span>
                  <Button onClick={logout} variant="ghost" size="sm" data-testid="logout-button">
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="hidden md:flex items-center space-x-2">
                  <Button onClick={() => handleAuthClick('login')} variant="ghost" size="sm" data-testid="login-button">
                    Login
                  </Button>
                  <Button onClick={() => handleAuthClick('register')} size="sm" data-testid="register-button">
                    Register
                  </Button>
                </div>
              )}

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-sm font-medium hover:text-secondary" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>
              <Link to="/products" className="block text-sm font-medium hover:text-secondary" onClick={() => setMobileMenuOpen(false)}>
                Products
              </Link>
              <Link to="/contact" className="block text-sm font-medium hover:text-secondary" onClick={() => setMobileMenuOpen(false)}>
                Contact
              </Link>
              {user && (
                <Link to="/orders" className="block text-sm font-medium hover:text-secondary" onClick={() => setMobileMenuOpen(false)}>
                  My Orders
                </Link>
              )}
              {user?.role === 'admin' ? (
                <Link to="/admin" className="block text-sm font-medium text-secondary bg-secondary/10 px-3 py-2 rounded-sm hover:bg-secondary/20" onClick={() => setMobileMenuOpen(false)}>
                  Admin Panel
                </Link>
              ) : (
                <Link to="/admin/login" className="block text-sm font-medium hover:text-secondary" onClick={() => setMobileMenuOpen(false)}>
                  Admin Login
                </Link>
              )}
              {!user && (
                <>
                  <Button onClick={() => { handleAuthClick('login'); setMobileMenuOpen(false); }} className="w-full" variant="outline">
                    Login
                  </Button>
                  <Button onClick={() => { handleAuthClick('register'); setMobileMenuOpen(false); }} className="w-full">
                    Register
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {showAuthModal && (
        <AuthModal mode={authMode} onClose={() => setShowAuthModal(false)} onSwitchMode={setAuthMode} />
      )}
    </>
  );
};

export default Navbar;